// style imports
import "../../styles/Dashboard/PowerEnergyCard.css";
// library imports
import { Segmented, Card, Empty, Skeleton, Flex, Tooltip, Grid } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useState, useEffect, useContext } from "react";
import dayjs from "dayjs";
// component imports
import { HorizontalDateMonthYearFilter } from "../common/DateFilter";
import CustomLineAreaChart from "../common/CustomLineAreaChart";
import CustomLineBarChart from "../common/CustomLineBarChart";
// hook imports
import useFetch from "../../hooks/useFetch";
// context imports
import { IndexContext } from "../../context/IndexContext";
// config imports
import {
  DARK_BLUE,
  LIGHT_BLUE,
  LIGHT_LIGHT_BLUE,
  DARK_GREEN,
  LIGHT_GREEN,
  ORANGE,
  DARK_THEME_DARK_BLUE,
  PURPLE,
} from "../../configs/configs";

const { useBreakpoint } = Grid;

const powerEnergyDateTypeOptions = [
  { label: "Day", value: "day" },
  { label: "Month", value: "month" },
  { label: "Year", value: "year" },
];

const powerTrendTypeOptions = [
  { label: "Power Demand", value: "powerDemand" },
  { label: "Power", value: "power" },
];

export default function PowerEnergyCard({
  powerEnergyDateSelection,
  setPowerEnergyDateSelection,
  powerEnergyDateTypeSelection,
  setPowerEnergyDateType,
}) {
  const screens = useBreakpoint();
  const isXxl = screens.xxl;

  const energyCardHeight = isXxl
    ? "calc((100vh - 58px - 50px - 24px - 126px - 38px)/2)" // 100vh - nav menu height - site selection height - gap - statistic graph height - outside card header height
    : "350px";
  const powerCardHeight = isXxl
    ? "calc((100vh - 58px - 50px - 24px - 126px - 38px - 48px)/2)" // 100vh - nav menu height - site selection height - gap - statistic graph height - outside card header height - random pixel to disable scrolling
    : "350px";

  const [fetchWrapper] = useFetch();
  const {
    hasPvPowerMeter,
    hasInverterSelected,
    siteSelected,
    notificationApi,
    isLightTheme,
    subscribeAiBess,
    hasGensetSelected,
    hasTnbSelected,
  } = useContext(IndexContext);

  const systemTypeOptions = [
    { label: "Overall", value: "overall" },
    ...(hasPvPowerMeter || hasInverterSelected
      ? [{ label: "PV System", value: "pv" }]
      : []),
  ];

  const [systemTypeSelection, setSystemTypeSelection] = useState(
    systemTypeOptions[0].value,
  );

  const [powerTrendTypeSelection, setPowerTrendTypeSelection] = useState(
    powerTrendTypeOptions[0].value,
  );

  const [siteCommissioningDate, setSiteCommissioningDate] = useState(null);

  // Chart Data
  // energy chart data
  const [energyChartData, setEnergyChartData] = useState([]);
  const [loadEnergyData, setLoadEnergyData] = useState([]);
  const [energyConsumptionLoading, setEnergyConsumptionLoading] =
    useState(false);

  // power chart data
  const [powerChartData, setPowerChartData] = useState([]);
  const [irradianceData, setIrradianceData] = useState([]);
  const [powerTrendLoading, setPowerTrendLoading] = useState(false);

  // power demand chart data
  const [powerDemandChartData, setPowerDemandChartData] = useState([]);
  const [loadPowerDemandData, setLoadPowerDemandData] = useState([]);
  const [powerDemandLoading, setPowerDemandLoading] = useState(false);

  const [chartRange, setChartRange] = useState([]);
  const [chartDomain, setChartDomain] = useState([]);

  /** API to get energy trending chart data */
  function getEnergyTrendingChartData() {
    setEnergyConsumptionLoading(true);

    fetchWrapper({
      endpoint_url: "dashboard/getEnergyTrendingChartData",
      method: "GET",
      set_content_type_header: false,
      params: {
        site_id: siteSelected,
        date_selection: powerEnergyDateTypeSelection,
        date_graph: powerEnergyDateSelection
          .startOf("day")
          .format("YYYY-MM-DD HH:mm:ss"),
        system_type: systemTypeSelection,
      },

      onSuccess: (response) => {
        setEnergyChartData(response.data.energy_trending_data_df);
        setLoadEnergyData(response.data.load_energy_df);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get energy trending chart data.",
          description: response.statusText,
        });
      },

      onFinish: () => {
        setEnergyConsumptionLoading(false);
      },
    });
  }

  /** API to get power demand trending chart data */
  function getPowerDemandTrendingChartData() {
    setPowerDemandLoading(true);

    fetchWrapper({
      endpoint_url: "dashboard/getPowerDemandTrendingChartData",
      method: "GET",
      set_content_type_header: false,
      params: {
        site_id: siteSelected,
        date_selection: powerEnergyDateTypeSelection,
        date_graph: powerEnergyDateSelection
          .startOf("day")
          .format("YYYY-MM-DD HH:mm:ss"),
        system_type: systemTypeSelection,
      },

      onSuccess: (response) => {
        setPowerDemandChartData(response.data.power_demand_trending_data_df);
        setLoadPowerDemandData(response.data.load_power_demand_df);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get power demand trending chart data.",
          description: response.statusText,
        });
      },

      onFinish: () => {
        setPowerDemandLoading(false);
      },
    });
  }

  /** API to get power chart data */
  function getPowerChartData() {
    setPowerTrendLoading(true);

    fetchWrapper({
      endpoint_url: "dashboard/getPowerChartData",
      method: "GET",
      set_content_type_header: false,
      params: {
        site_id: siteSelected,
        date_graph: powerEnergyDateSelection
          .startOf("day")
          .format("YYYY-MM-DD HH:mm:ss"),
        system_type: systemTypeSelection,
      },

      onSuccess: (response) => {
        setPowerChartData(response.data.kWT_df);
        setIrradianceData(response.data.irradiance_df);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get power chart data.",
          description: response.statusText,
        });
      },

      onFinish: () => {
        setPowerTrendLoading(false);
      },
    });
  }

  /* API to get site commissioning date */
  function getSiteCommissioningDate() {
    fetchWrapper({
      endpoint_url: "common/getSiteCommissioningDate",
      method: "GET",
      set_content_type_header: false,
      params: {
        site_id: siteSelected,
      },

      onSuccess: (response) => {
        setSiteCommissioningDate(response.data.site_commissioning_date);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get site commissioning date.",
          description: response.statusText,
        });
      },
    });
  }

  // Use Effect
  useEffect(() => {
    if (siteSelected && powerEnergyDateSelection) {
      setEnergyChartData([]);
      setLoadEnergyData([]);
      setPowerDemandChartData([]);
      setLoadPowerDemandData([]);
      getEnergyTrendingChartData();
      getPowerDemandTrendingChartData();
      if (powerTrendTypeSelection === "power") {
        getPowerChartData();
      }

      const timer = setInterval(
        () => {
          setEnergyChartData([]);
          setLoadEnergyData([]);
          setPowerDemandChartData([]);
          setLoadPowerDemandData([]);
          getEnergyTrendingChartData();
          getPowerDemandTrendingChartData();
          getPowerChartData();
        },
        30 * 60 * 1000,
      ); // refresh every 30 mins

      return () => {
        clearInterval(timer);
      };
    }
  }, [
    siteSelected,
    powerEnergyDateTypeSelection,
    powerEnergyDateSelection,
    systemTypeSelection,
    powerTrendTypeSelection,
  ]);

  useEffect(() => {
    if (siteSelected && powerEnergyDateSelection) {
      setPowerChartData([]);
      setIrradianceData([]);
      if (powerTrendTypeSelection === "power") {
        getPowerChartData();
      }
    }
  }, [
    siteSelected,
    powerEnergyDateSelection,
    systemTypeSelection,
    powerTrendTypeSelection,
  ]);

  useEffect(() => {
    if (systemTypeSelection === "overall") {
      if (subscribeAiBess === true) {
        if (hasGensetSelected === true) {
          if (hasTnbSelected === false) {
            setChartDomain([
              "PV (Self-Consumption)",
              "BESS Discharge",
              "BESS Charge",
              "Load",
              "Genset",
            ]);
            setChartRange(
              isLightTheme
                ? [DARK_GREEN, LIGHT_BLUE, LIGHT_LIGHT_BLUE, ORANGE, PURPLE]
                : [DARK_GREEN, LIGHT_BLUE, LIGHT_LIGHT_BLUE, ORANGE, PURPLE],
            );
          } else {
            setChartDomain([
              "PV (Self-Consumption)",
              "PV (Export)",
              "BESS Discharge",
              "BESS Charge",
              "TNB",
              "Load",
              "Genset",
            ]);
            setChartRange(
              isLightTheme
                ? [
                    DARK_GREEN,
                    LIGHT_GREEN,
                    LIGHT_BLUE,
                    LIGHT_LIGHT_BLUE,
                    DARK_BLUE,
                    ORANGE,
                    PURPLE,
                  ]
                : [
                    DARK_GREEN,
                    LIGHT_GREEN,
                    LIGHT_BLUE,
                    LIGHT_LIGHT_BLUE,
                    DARK_THEME_DARK_BLUE,
                    ORANGE,
                    PURPLE,
                  ],
            );
          }
        } else {
          setChartDomain([
            "PV (Self-Consumption)",
            "PV (Export)",
            "BESS Discharge",
            "BESS Charge",
            "TNB",
            "Load",
          ]);
          setChartRange(
            isLightTheme
              ? [
                  DARK_GREEN,
                  LIGHT_GREEN,
                  LIGHT_BLUE,
                  LIGHT_LIGHT_BLUE,
                  DARK_BLUE,
                  ORANGE,
                ]
              : [
                  DARK_GREEN,
                  LIGHT_GREEN,
                  LIGHT_BLUE,
                  LIGHT_LIGHT_BLUE,
                  DARK_THEME_DARK_BLUE,
                  ORANGE,
                ],
          );
        }
      } else {
        if (hasGensetSelected === true) {
          setChartDomain([
            "PV (Self-Consumption)",
            "PV (Export)",
            "TNB",
            "Load",
            "Genset",
          ]);
          setChartRange(
            isLightTheme
              ? [DARK_GREEN, LIGHT_GREEN, DARK_BLUE, ORANGE, PURPLE]
              : [DARK_GREEN, LIGHT_GREEN, DARK_THEME_DARK_BLUE, ORANGE, PURPLE],
          );
        }
      }
    } else {
      if (hasTnbSelected === true) {
        setChartDomain(["PV (Self-Consumption)", "PV (Export)"]);
        setChartRange([DARK_GREEN, LIGHT_GREEN]);
      } else {
        setChartDomain(["PV (Self-Consumption)"]);
        setChartRange([DARK_GREEN]);
      }
    }
  }, [subscribeAiBess, hasGensetSelected, hasTnbSelected, systemTypeSelection]);

  useEffect(() => {
    if (siteSelected) {
      getSiteCommissioningDate();
    }
  }, [siteSelected]);

  return (
    <>
      <Card
        size="small"
        className="h-100"
        title={
          <Flex justify="space-between" align="center" wrap={true}>
            <Segmented
              options={systemTypeOptions}
              onChange={(value) => {
                setSystemTypeSelection(value);
              }}
              value={systemTypeSelection}
              disabled={
                energyConsumptionLoading ||
                powerTrendLoading ||
                powerDemandLoading
              }
            />
            <Flex gap={4}>
              <HorizontalDateMonthYearFilter
                startDateSelection={powerEnergyDateSelection}
                setStartDateSelection={setPowerEnergyDateSelection}
                onFilter={() => {}}
                pickerType={powerEnergyDateTypeSelection}
                showTitle={false}
                minDate={dayjs(siteCommissioningDate, "YYYY-MM-DD")}
                maxDate={dayjs()}
                disabled={
                  energyConsumptionLoading ||
                  powerTrendLoading ||
                  powerDemandLoading
                }
              />
              {/* <Segmented
                options={powerEnergyDateTypeOptions}
                onChange={(value) => {
                  setPowerEnergyDateType(value);
                }}
                value={powerEnergyDateTypeSelection}
                disabled={
                  energyConsumptionLoading ||
                  powerTrendLoading ||
                  powerDemandLoading
                }
              /> */}
            </Flex>
          </Flex>
        }
      >
        <Flex vertical={true}>
          <Card
            size="small"
            bordered={false}
            style={{ height: energyCardHeight }}
            styles={{ body: { height: "100%" } }}
          >
            <Flex justify="space-between">
              <span className="energy-title">Energy Consumption</span>
              <span>
                <Tooltip
                  title={
                    "The values of these charts are updated every 30 minutes."
                  }
                >
                  <InfoCircleOutlined />
                </Tooltip>
              </span>
            </Flex>

            {systemTypeSelection == "pv" ? (
              <div
                style={{
                  height: "calc(100% - 32px)", // 100% - title height
                }}
              >
                <div
                  style={{
                    display: energyConsumptionLoading
                      ? "none"
                      : energyChartData.length == 0
                        ? "none"
                        : "block",
                    height: "100%",
                  }}
                >
                  <CustomLineBarChart
                    dataLine={[]}
                    dataBar={energyChartData.map((item) => {
                      const newItem = { ...item };
                      for (const key in newItem) {
                        if (newItem[key] === "-") {
                          newItem[key] = null;
                        }
                      }
                      return newItem;
                    })}
                    xAxisLine="time"
                    yAxisLine="Load"
                    xAxisBar="time"
                    yAxisBar="value"
                    xAxisTitleBar="Time"
                    yAxisTitleBar="Energy (kWh)"
                    barColor="type"
                    barChartRange={chartRange}
                    barChartDomain={chartDomain}
                  />
                </div>

                <Skeleton
                  size="small"
                  active
                  style={{
                    display: energyConsumptionLoading ? "table" : "none",
                  }}
                  paragraph={{ rows: 4 }}
                />

                <Empty
                  style={{
                    padding: "40px 0px",
                    display:
                      energyChartData.length == 0
                        ? energyConsumptionLoading
                          ? "none"
                          : "block"
                        : "none",
                  }}
                />
              </div>
            ) : (
              <div
                style={{
                  height: "calc(100% - 32px)", // 100% - title height
                }}
              >
                <Empty
                  style={{
                    padding: "40px 0px",
                    display:
                      energyChartData.length == 0
                        ? energyConsumptionLoading
                          ? "none"
                          : "block"
                        : "none",
                  }}
                />

                <div
                  style={{
                    display: energyConsumptionLoading
                      ? "none"
                      : energyChartData.length == 0
                        ? "none"
                        : "block",
                    height: "100%",
                  }}
                >
                  <CustomLineBarChart
                    dataLine={loadEnergyData.map((item) => {
                      const newItem = { ...item };
                      for (const key in newItem) {
                        if (newItem[key] === "-") {
                          newItem[key] = null;
                        }
                      }
                      return newItem;
                    })}
                    dataBar={energyChartData.map((item) => {
                      const newItem = { ...item };
                      for (const key in newItem) {
                        if (newItem[key] === "-") {
                          newItem[key] = null;
                        }
                      }
                      return newItem;
                    })}
                    xAxisLine="time"
                    yAxisLine="Load"
                    xAxisBar="time"
                    yAxisBar="value"
                    xAxisTitleBar="Time"
                    yAxisTitleBar="Energy (kWh)"
                    lineColor="type"
                    barColor="type"
                    barChartRange={chartRange}
                    barChartDomain={chartDomain}
                    lineChartColor={ORANGE}
                  />
                </div>

                <Skeleton
                  size="small"
                  active
                  style={{
                    display: energyConsumptionLoading ? "table" : "none",
                  }}
                  paragraph={{ rows: 4 }}
                />
              </div>
            )}
          </Card>

          <Card
            size="small"
            bordered={false}
            style={{ height: powerCardHeight }}
            styles={{ body: { height: "100%" } }}
          >
            <Flex vertical={true} gap={4}>
              <span className="power-title">Power Trends</span>

              {powerEnergyDateTypeSelection === "day" ? (
                <Segmented
                  block
                  options={powerTrendTypeOptions}
                  onChange={(value) => {
                    setPowerTrendTypeSelection(value);
                  }}
                  value={powerTrendTypeSelection}
                  style={{ marginBottom: 10, width: isXxl ? "260px" : "100%" }}
                  disabled={powerTrendLoading || powerDemandLoading}
                />
              ) : (
                <></>
              )}
            </Flex>

            {powerEnergyDateTypeSelection == "day" &&
            powerTrendTypeSelection === "power" ? (
              <div
                style={{
                  height: "calc(100% - 72px)", // 100% - title height
                }}
              >
                <div
                  style={{
                    display: powerTrendLoading
                      ? "none"
                      : powerChartData.length == 0
                        ? "none"
                        : "block",
                    height: "100%",
                  }}
                >
                  <CustomLineAreaChart
                    dataLine={powerChartData.map((item) => {
                      const newItem = { ...item };
                      for (const key in newItem) {
                        if (newItem[key] === "-") {
                          newItem[key] = null;
                        }
                      }
                      return newItem;
                    })}
                    xLine="date"
                    yLine="value"
                    colorLine="type1"
                    xAxisTitleLine="Time"
                    yAxisTitleLine="Power (kW)"
                    chartDomainLine={
                      systemTypeSelection === "overall"
                        ? [
                            "Power (TNB)",
                            "Power (PV)",
                            "Power (Load)",
                            "Power (BESS)",
                            "Power (Genset)",
                            "Irradiance",
                          ]
                        : ["Power (PV)", "Irradiance"]
                    }
                    chartRangeLine={
                      systemTypeSelection === "overall"
                        ? [
                            isLightTheme ? DARK_BLUE : DARK_THEME_DARK_BLUE,
                            DARK_GREEN,
                            ORANGE,
                            LIGHT_BLUE,
                            PURPLE,
                            "gray",
                          ]
                        : [DARK_GREEN, "gray"]
                    }
                    dataArea={irradianceData.map((item) => {
                      const newItem = { ...item };
                      for (const key in newItem) {
                        if (newItem[key] === "-") {
                          newItem[key] = null;
                        }
                      }
                      return newItem;
                    })}
                    xArea="date"
                    yArea="value"
                    colorArea="type"
                    yAxisTitleArea={`Irradiance (W/m\u00B2)`}
                  />
                </div>

                <Skeleton
                  size="small"
                  active
                  style={{ display: powerTrendLoading ? "table" : "none" }}
                  paragraph={{ rows: 4 }}
                />

                <Empty
                  style={{
                    padding: "40px 0px",
                    display:
                      powerChartData.length == 0
                        ? powerTrendLoading
                          ? "none"
                          : "block"
                        : "none",
                  }}
                />
              </div>
            ) : (
              <div
                style={{
                  height: "calc(100% - 32px)", // 100% - title height
                }}
              >
                {systemTypeSelection === "pv" ? (
                  <div style={{ height: "100%" }}>
                    <Empty
                      style={{
                        padding: "40px 0px",
                        display:
                          powerDemandChartData.length == 0
                            ? powerDemandLoading
                              ? "none"
                              : "block"
                            : "none",
                      }}
                    />

                    <Skeleton
                      size="small"
                      active
                      style={{
                        display: powerDemandLoading ? "table" : "none",
                      }}
                      paragraph={{ rows: 4 }}
                    />

                    <div
                      style={{
                        display: powerDemandLoading
                          ? "none"
                          : powerDemandChartData.length == 0
                            ? "none"
                            : "block",
                        height: "100%",
                      }}
                    >
                      <CustomLineBarChart
                        dataLine={[]}
                        dataBar={powerDemandChartData.map((item) => {
                          const newItem = { ...item };
                          for (const key in newItem) {
                            if (newItem[key] === "-") {
                              newItem[key] = null;
                            }
                          }
                          return newItem;
                        })}
                        xAxisBar="time"
                        yAxisBar="value"
                        xAxisTitleBar="Time"
                        yAxisTitleBar="Power Demand (kW)"
                        barColor="type"
                        barChartRange={chartRange}
                        barChartDomain={chartDomain}
                      />
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      height: "100%",
                    }}
                  >
                    <div
                      style={{
                        display: powerDemandLoading
                          ? "none"
                          : powerDemandChartData.length == 0
                            ? "none"
                            : "block",
                        height: "100%",
                      }}
                    >
                      <CustomLineBarChart
                        dataLine={loadPowerDemandData.map((item) => {
                          const newItem = { ...item };
                          for (const key in newItem) {
                            if (newItem[key] === "-") {
                              newItem[key] = null;
                            }
                          }
                          return newItem;
                        })}
                        dataBar={powerDemandChartData.map((item) => {
                          const newItem = { ...item };
                          for (const key in newItem) {
                            if (newItem[key] === "-") {
                              newItem[key] = null;
                            }
                          }
                          return newItem;
                        })}
                        xAxisLine="time"
                        yAxisLine="Load"
                        xAxisBar="time"
                        yAxisBar="value"
                        xAxisTitleBar="Time"
                        yAxisTitleBar="Power Demand (kW)"
                        lineColor="type"
                        barColor="type"
                        barChartRange={chartRange}
                        barChartDomain={chartDomain}
                        lineChartColor={ORANGE}
                      />
                    </div>

                    <Skeleton
                      size="small"
                      active
                      style={{
                        display: powerDemandLoading ? "table" : "none",
                      }}
                      paragraph={{ rows: 4 }}
                    />

                    <Empty
                      style={{
                        padding: "40px 0px",
                        display:
                          powerDemandChartData.length == 0
                            ? powerDemandLoading
                              ? "none"
                              : "block"
                            : "none",
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </Card>
        </Flex>
      </Card>
    </>
  );
}
